import { useEffect, useRef } from 'react';
import { usePlaybackStore } from '@/stores';
import { audioTimeFor, transportTimeAt } from '@/utils/playbackClock';
import { getAudioNow } from '@/utils/audioNow';
import {
  selectChords,
  lastChordIndexStrictlyBefore,
  LOOKAHEAD_S,
  POLL_MS,
  LATE_GRACE_S,
} from '@/utils/noteScheduler';
import { getNoteHand } from '@/utils/hand';
import type { Chord, ScoreTimeline } from '@/types';

interface UseAutoPlayCallbacks {
  playNote: (pitch: number, velocity: number, time?: number, cutoffAfterS?: number) => void;
  stopNote: (pitch: number, time?: number) => void;
  stopAllNotes: () => void;
  /** Cancels queued-but-not-yet-fired attacks WITHOUT releasing sounding
   *  notes (OTO-22 §2b) -- used ONLY by the tempo-change reconciliation
   *  below, which must re-time queued attacks without interrupting notes
   *  already sounding. Every other reconciliation path silences via
   *  `stopAllNotes`, which composes this internally (see `usePianoAudio`). */
  cancelScheduled: () => void;
}

/**
 * OTO-21 (Gate B fix): pure helper -- index of the last chord (assumed
 * sorted by `startTime`) whose `startTime` is at or before `currentTime`,
 * or -1 if none qualify. Used to re-derive the auto-play scheduling cursor
 * from the actual transport position rather than trusting a locally-tracked
 * counter, so a seek (forward or backward, including loop-back) never
 * replays chords already behind the new position and never skips chords
 * ahead of it.
 *
 * OTO-22: still the cursor rule for every reconciliation EXCEPT a loop wrap
 * (M1), which uses the strict-`<` `lastChordIndexStrictlyBefore`
 * (`utils/noteScheduler.ts`) instead -- this `<=` rule would otherwise mark
 * a chord sitting exactly at `loopStart` as already played and drop it on
 * every pass. Internals unchanged from OTO-21.
 */
export function findLastPlayedChordIndex(chords: Chord[], currentTime: number): number {
  let lastIndex = -1;
  for (let i = 0; i < chords.length; i++) {
    if (chords[i].startTime <= currentTime) {
      lastIndex = i;
    } else {
      break;
    }
  }
  return lastIndex;
}

/**
 * OTO-22 (core fix): audio-clock lookahead scheduler, replacing OTO-21's
 * store-tick fire-loop.
 *
 * Two-clock problem: the old model scheduled a chord the instant the
 * *visual* transport (`position.currentTime`, an rAF-derived value) crossed
 * its `startTime` inside the store subscription -- fine at a steady 60fps,
 * but a main-thread stall meant many chords' windows were crossed in a
 * single delayed tick once the frame finally painted, firing them all
 * back-to-back (the note-burst bug this ticket fixes).
 *
 * Here a `setInterval(POLL_MS)` poll -- independent of rAF/paint -- reads
 * the audio clock directly each tick (`transportTimeAt(anchor,
 * getAudioNow())`, see `utils/playbackClock.ts`) and schedules any chord
 * whose `startTime` falls within a short `[windowStart, windowStart +
 * LOOKAHEAD_S]` lookahead window at its exact audio-clock time
 * (`audioTimeFor`, never `Tone.now()`), via `usePianoAudio`'s
 * future-scheduling path (T3). A stall longer than the window means some
 * chords' `startTime` are now older than `windowStart - LATE_GRACE_S`;
 * those are skipped (logged once per tick, never fired) rather than
 * burst-played on recovery -- this is the late-skip-not-burst policy; see
 * `utils/noteScheduler.ts`'s `selectChords` for the pure selection logic
 * this polls against.
 *
 * Discontinuity reconciliation (§2b): every event that stops, silences, or
 * discontinuously moves the transport (pause, stop, freeze, seek, loop
 * wrap) goes through ONE shared path below (`reconcile`) -- cancel any
 * not-yet-fired queued attack, then re-derive the cursor from the
 * reconciled position -- so a chord queued ahead of "now" at the moment of
 * the discontinuity is never silently dropped nor double-fired once things
 * resume. Only a mid-flight tempo change reconciles WITHOUT silencing
 * (`cancelScheduled`, never `stopAllNotes`): notes already sounding must
 * keep ringing (and keep their tempo-blind scheduled releases, contract
 * 12) through a tempo change -- only their not-yet-fired siblings are
 * cancelled and re-timed at the new tempo.
 *
 * Loop wrap (M1) is the one reconciliation that seeds the cursor with
 * `lastChordIndexStrictlyBefore` instead of `findLastPlayedChordIndex`, and
 * is checked BEFORE the generic seek branch in the store subscription --
 * `wrapToLoopStart` also resets `playedNotes` (like a seek does), so
 * without this ordering a wrap would either be reconciled twice or take the
 * seek's `<=` cursor rule and drop the chord sitting exactly at `loopStart`
 * on every pass.
 */
export function useAutoPlay(
  timeline: ScoreTimeline | null | undefined,
  { playNote, stopAllNotes, cancelScheduled }: UseAutoPlayCallbacks
) {
  const timelineRef = useRef(timeline);
  timelineRef.current = timeline;

  const cursorRef = useRef<number>(-1);
  // Loop-end cache (measure number -> transport time): mirrors the same
  // cache in `usePlaybackEngine.ts` -- `find()` over `tl.measures` only
  // needs to rerun when the timeline or the configured loop-end measure
  // number actually changes, not on every poll tick.
  const loopEndCacheRef = useRef<{
    timeline: ScoreTimeline;
    loopEnd: number;
    loopEndTime: number;
  } | null>(null);
  // B1/B4: tracks whether `reconcile` has run at least once since the
  // transport last entered 'playing'. The forward safety margin below
  // (B4) must apply ONLY after a reconciliation (wrap/pause/seek/tempo
  // change) has re-derived the cursor from a windowStart-clamped
  // position -- the exact scenario where `playNote`'s later re-read of
  // the audio clock can tip an exactly-"now" attack into the immediate
  // branch and silently skip seam recording (T8, loop-wrap boundary
  // chords). Applying it on the very first stopped->playing tick as well
  // (no prior reconcile) wrongly forced that tick's chords onto the
  // scheduled path too, regressing B1 (which asserts the immediate-path
  // behavior on a fresh start).
  const reconciledRef = useRef(false);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const clearPoll = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // §2b: the single discontinuity-reconciliation path. `cursorRule` picks
    // `findLastPlayedChordIndex` (everywhere) or `lastChordIndexStrictlyBefore`
    // (wrap only, M1); `silence` is false ONLY for the tempo-change path.
    const reconcile = (
      currentTime: number,
      cursorRule: (chords: Chord[], time: number) => number,
      silence: boolean
    ) => {
      if (silence) {
        stopAllNotes();
      } else {
        cancelScheduled();
      }
      const tl = timelineRef.current;
      cursorRef.current = tl ? cursorRule(tl.chords, currentTime) : -1;
      reconciledRef.current = true;
    };

    const tick = () => {
      const s = usePlaybackStore.getState();
      const tl = timelineRef.current;
      if (!tl || s.state !== 'playing' || s.config.waitMode || s.transportFrozen) return;

      // A straggler tick can in principle observe a null anchor for one
      // cycle around a transport discontinuity -- a normal frozen-transport
      // no-op, not an error; see the null-anchor carve-out documented on
      // `playbackClock.ts`. Fail-fast on scheduling errors below, but this
      // is a legitimate state, not one.
      const anchor = s.anchor;
      if (!anchor) return;

      const audioNow = getAudioNow();
      const windowStart = transportTimeAt(anchor, audioNow);
      const windowEnd = windowStart + LOOKAHEAD_S;

      // Loop-end clamp (B1): while a loop region is active, never schedule
      // a chord at or past `loopEnd` -- the wrap resync (below) re-arms the
      // next pass once the engine detects the boundary and calls
      // `wrapToLoopStart`.
      let hardStop: number | undefined;
      if (s.config.loopStart !== null && s.config.loopEnd !== null) {
        const cache = loopEndCacheRef.current;
        if (cache && cache.timeline === tl && cache.loopEnd === s.config.loopEnd) {
          hardStop = cache.loopEndTime;
        } else {
          hardStop =
            tl.measures.find((m) => m.number === s.config.loopEnd)?.startTime ?? tl.totalDuration;
          loopEndCacheRef.current = {
            timeline: tl,
            loopEnd: s.config.loopEnd,
            loopEndTime: hardStop,
          };
        }
      }

      let toSchedule: ReturnType<typeof selectChords>['toSchedule'];
      let toSkip: ReturnType<typeof selectChords>['toSkip'];
      let nextCursor: number;
      try {
        ({ toSchedule, toSkip, nextCursor } = selectChords({
          chords: tl.chords,
          cursor: cursorRef.current,
          windowStart,
          windowEnd,
          grace: LATE_GRACE_S,
          hardStop,
        }));
      } catch (err) {
        console.error('[useAutoPlay/tick] Failed: chord selection error | reason=', err);
        throw err;
      }

      if (toSkip.length > 0) {
        console.warn(
          `[useAutoPlay/tick] Skipped ${toSkip.length} stale chord(s) at transport time ` +
            `${windowStart.toFixed(3)}s (stall recovery, late-skip-not-burst policy)`
        );
      }

      for (const { index, transportTime } of toSchedule) {
        const chord = tl.chords[index];
        // A chord in `selectChords`'s "late but not stale" zone is clamped
        // to `transportTime: windowStart`, which maps back through
        // `audioTimeFor` to (very close to) this tick's own `audioNow` --
        // by design, it means "fire ASAP". But `playNote` independently
        // re-reads the audio clock moments later to decide immediate vs.
        // scheduled dispatch (see its own clock-authority comment); the real,
        // nonzero JS time this loop/filter/forEach takes between here and
        // that re-read is enough to tip an exactly-"now" attack into
        // `time <= audioNow`, which fires correctly but silently skips the
        // scheduled-path's seam recording (T8 caught this on every loop-wrap
        // boundary chord, whose window always opens via exactly this clamp).
        // A tiny forward floor -- comfortably under both realistic per-tick
        // JS overhead and the scheduling-precision tolerances other T8
        // scenarios assert on -- guarantees the scheduled path wins without
        // measurably delaying the attack.
        // The floor only makes sense for a chord `selectChords` actually
        // clamped into the grace zone (`transportTime === windowStart`
        // because the chord's own `startTime` fell behind it) -- an
        // on-schedule chord (`transportTime === chord.startTime`, e.g. a
        // seek landing exactly on a chord boundary) has no "past-now"
        // ambiguity to guard against, and flooring it would just delay an
        // otherwise-correct attack time for no reason. B1: also gate on
        // `reconciledRef` (see above) so a fresh start's first tick is
        // never floored even if its opening chord happens to be clamped.
        const wasClamped = chord.startTime < windowStart;
        const audioTime =
          wasClamped && reconciledRef.current
            ? Math.max(audioTimeFor(anchor, transportTime), audioNow + 0.008)
            : audioTimeFor(anchor, transportTime);

        const notesToPlay = chord.notes.filter((note) => {
          if (s.config.hand === 'both') return true;
          return getNoteHand(note) === s.config.hand;
        });

        notesToPlay.forEach((note) => {
          // OTO-29: velocity 0 must skip playback entirely, not fall through to default
          if (note.velocity === 0) return;

          const velocity = note.velocity / 127;
          // Contract 10/12: sustain true schedules NO release at all;
          // sustain false schedules a per-handle release at exactly
          // `attackTime + note.duration` -- tempo-blind, an intentionally
          // preserved OTO-21 quirk (NOT `duration / tempoMultiplier`).
          playNote(note.pitch, velocity, audioTime, s.config.sustain ? undefined : note.duration);
        });
      }

      cursorRef.current = nextCursor;
    };

    const shouldPoll = (s: ReturnType<typeof usePlaybackStore.getState>) =>
      s.state === 'playing' && !s.config.waitMode && !s.transportFrozen;

    const startPoll = () => {
      if (intervalId === null) intervalId = setInterval(tick, POLL_MS);
    };

    const initial = usePlaybackStore.getState();
    let prevState = initial.state;
    let prevPlayedNotes = initial.playedNotes;
    let prevLoopWrapEpoch = initial.loopWrapEpoch;
    let prevTransportFrozen = initial.transportFrozen;
    let prevTempoMultiplier = initial.config.tempoMultiplier;
    if (shouldPoll(initial)) startPoll();

    // Kept for signals ONLY (OTO-22): state transitions, `playedNotes`
    // identity (seek), `loopWrapEpoch` (wrap), `transportFrozen`
    // (freeze/thaw, T7), `config.tempoMultiplier` (tempo reconciliation).
    // The per-tick chord-firing work that used to live here moved to the
    // `setInterval` poll above.
    const unsubscribe = usePlaybackStore.subscribe((s) => {
      const leavingPlaying = prevState === 'playing' && s.state !== 'playing';
      // B1: a fresh stopped->playing transition has no reconciliation
      // behind it yet (cursor starts at -1, no boundary was just
      // re-derived) -- clear the flag so the first tick's chords take the
      // ordinary immediate/scheduled branch on their own merits, same as
      // before the B4 margin existed. Resuming from paused is untouched:
      // pause's own leavingPlaying reconcile above already set the flag,
      // and that reconciliation is exactly what the margin protects on
      // the resume tick.
      const enteringFromStopped = prevState === 'stopped' && s.state === 'playing';
      prevState = s.state;
      if (enteringFromStopped) reconciledRef.current = false;

      const wrapped = s.loopWrapEpoch !== prevLoopWrapEpoch;
      prevLoopWrapEpoch = s.loopWrapEpoch;

      const playedNotesReset = s.playedNotes !== prevPlayedNotes;
      prevPlayedNotes = s.playedNotes;

      const frozenRisingEdge = s.transportFrozen && !prevTransportFrozen;
      prevTransportFrozen = s.transportFrozen;

      const tempoChanged = s.config.tempoMultiplier !== prevTempoMultiplier;
      prevTempoMultiplier = s.config.tempoMultiplier;

      // Leaving playback (pause/stop/wait): silence immediately and roll
      // the cursor back to the frozen position -- replaces OTO-21's
      // cursor-preserve-on-pause (see the named test "queued-then-cancelled
      // chords re-arm on resume", M3). Only a true stop additionally
      // rewinds it to -1 (existing OTO-21 semantics).
      if (leavingPlaying) {
        clearPoll();
        reconcile(s.position.currentTime, findLastPlayedChordIndex, true);
        if (s.state === 'stopped') cursorRef.current = -1;
      }

      // Loop wrap (M1) -- MUST be checked before the seek branch below:
      // `wrapToLoopStart` also resets `playedNotes` in the same update, and
      // reconciling it via the seek's `<=` rule would mark the chord at
      // `loopStart` as already played and drop it on every pass.
      //
      // Follow-up fix (manual e2e QA, 2026-07-16): reconcile against
      // `s.loopWrapBoundaryTime` (the pure measure-boundary time), NOT
      // `s.position.currentTime` (the actual landing time, which always
      // drifts slightly past the boundary -- see field doc on the store).
      // Using the drifted position here made `lastChordIndexStrictlyBefore`
      // see the boundary chord's `startTime` as strictly before it too,
      // silently dropping that chord on every single wrap pass.
      if (wrapped) {
        reconcile(
          s.loopWrapBoundaryTime ?? s.position.currentTime,
          lastChordIndexStrictlyBefore,
          true
        );
      } else if (playedNotesReset && s.state !== 'stopped' && !leavingPlaying) {
        // A seek is independent of a state transition (OTO-21): `seek()`
        // resets `playedNotes` but never touches `state`, so seeking while
        // paused/waiting hits neither the leaving-playback branch above nor
        // a wrap. `stop()` also resets `playedNotes`, but `leavingPlaying`
        // already reconciled that case above -- skip here to avoid double
        // reconciliation.
        reconcile(s.position.currentTime, findLastPlayedChordIndex, s.state === 'playing');
      }

      // Tab hidden while playing (T7 wiring) -- freeze reconciliation (R1/M3).
      if (frozenRisingEdge) {
        clearPoll();
        reconcile(s.position.currentTime, findLastPlayedChordIndex, true);
      }

      // Mid-flight tempo change (M3): cancel queued attacks WITHOUT
      // silencing sounding notes (their scheduled releases stay valid, and
      // tempo-blind per contract 12), then re-derive the cursor from the
      // position under the NEW anchor so the next tick re-times the same
      // chords at the new tempo rather than skipping or double-firing them.
      if (tempoChanged && s.state === 'playing' && !leavingPlaying) {
        const derived = s.anchor
          ? transportTimeAt(s.anchor, getAudioNow())
          : s.position.currentTime;
        reconcile(derived, findLastPlayedChordIndex, false);
      }

      if (shouldPoll(s)) {
        startPoll();
      } else {
        clearPoll();
      }
    });

    return () => {
      unsubscribe();
      clearPoll();
      // OTO-21 Gate B fix (preserved): unmounting mid-playback must not
      // leave any currently-sounding notes stuck on.
      stopAllNotes();
    };
  }, [playNote, stopAllNotes, cancelScheduled]);
}
